import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import ContactPanel from '../components/ContactPanel';
import { useAuthFetch } from '../hooks/useAuthFetch';
import { useFilters } from '../hooks/useFilters';

const inputStyle = { padding: '8px 12px', borderRadius: 8, background: '#1a1d2e', border: '1px solid #2a2d3e', color: '#e2e8f0', fontSize: 13, fontFamily: 'inherit', outline: 'none' };

export default function ContactsPage() {
  const fetchWithAuth = useAuthFetch();
  const location = useLocation();
  const { search, setSearch, page, setPage, limit, setLimit, sortField, sortDir, toggleSort, filters, setFilter, buildQuery } = useFilters({ sortField: 'nom' });
  const [contacts, setContacts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(location.state?.contactId || null);
  const [showModal, setShowModal] = useState(false);
  const [clients, setClients] = useState([]);
  const [newContact, setNewContact] = useState({ client_id: '', prenom: '', nom: '', email: '', telephone: '', mobile: '', poste: '', principal: 0 });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchWithAuth('/api/clients?limit=999').then(r => r.json()).then(d => setClients(d.data || []));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`/api/contacts?${buildQuery()}`);
      const data = await res.json();
      setContacts(data.data || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [search, page, limit, sortField, sortDir, JSON.stringify(filters)]);

  useEffect(() => { load(); }, [load]);

  const createContact = async () => {
    const errors = {};
    if (!newContact.nom.trim()) errors.nom = true;
    if (!newContact.client_id) errors.client_id = true;
    if (Object.keys(errors).length) { setFormErrors(errors); return; }
    await fetchWithAuth('/api/contacts', { method: 'POST', body: JSON.stringify(newContact) });
    setShowModal(false);
    setNewContact({ client_id: '', prenom: '', nom: '', email: '', telephone: '', mobile: '', poste: '', principal: 0 });
    setFormErrors({});
    load();
  };

  const SortTh = ({ field, children }) => (
    <th onClick={() => toggleSort(field)} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}>
      {children} {sortField === field ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </th>
  );

  const totalPages = Math.ceil(total / limit);

  return (
    <div style={{ padding: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Contacts</h1>
        <button onClick={() => setShowModal(true)} style={{ padding: '9px 18px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>+ Nouveau contact</button>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Rechercher..." style={{ ...inputStyle, minWidth: 200 }} />
        <select value={filters.client_id || ''} onChange={e => setFilter('client_id', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
          <option value="">Tous les clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} style={{ ...inputStyle, cursor: 'pointer' }}>
          {[25, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
        </select>
      </div>

      <div style={{ background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #2a2d3e' }}>
                <SortTh field="nom">Nom</SortTh>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Société</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Poste</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Email</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Téléphone</th>
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Principal</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Chargement...</td></tr>
              ) : contacts.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 30, textAlign: 'center', color: '#64748b' }}>Aucun contact trouvé</td></tr>
              ) : contacts.map((c, i) => (
                <tr key={c.id} onClick={() => setSelectedId(c.id)} style={{ borderBottom: '1px solid #2a2d3e', cursor: 'pointer', background: i % 2 === 0 ? 'transparent' : '#ffffff05' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#41B07315'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : '#ffffff05'}>
                  <td style={{ padding: '11px 14px', fontSize: 13, color: '#e2e8f0', fontWeight: 500 }}>{c.prenom} {c.nom}</td>
                  <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{c.client_nom || '—'}</td>
                  <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{c.poste || '—'}</td>
                  <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{c.email || '—'}</td>
                  <td style={{ padding: '11px 14px', fontSize: 13, color: '#94a3b8' }}>{c.telephone || c.mobile || '—'}</td>
                  <td style={{ padding: '11px 14px' }}>{c.principal ? <span style={{ color: '#f59e0b', fontSize: 14 }}>⭐</span> : <span style={{ color: '#2a2d3e' }}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #2a2d3e' }}>
          <span style={{ fontSize: 13, color: '#64748b' }}>{total} contact{total !== 1 ? 's' : ''}</span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: '5px 12px', borderRadius: 6, background: 'transparent', border: '1px solid #2a2d3e', color: page === 1 ? '#2a2d3e' : '#94a3b8', cursor: page === 1 ? 'default' : 'pointer', fontSize: 13 }}>←</button>
            <span style={{ padding: '5px 12px', fontSize: 13, color: '#94a3b8' }}>Page {page} / {totalPages || 1}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} style={{ padding: '5px 12px', borderRadius: 6, background: 'transparent', border: '1px solid #2a2d3e', color: page >= totalPages ? '#2a2d3e' : '#94a3b8', cursor: page >= totalPages ? 'default' : 'pointer', fontSize: 13 }}>→</button>
          </div>
        </div>
      </div>

      {selectedId && <ContactPanel contactId={selectedId} onClose={() => { setSelectedId(null); load(); }} />}

      {showModal && (
        <>
          <div onClick={() => setShowModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 200 }} />
          <div className="modal-scroll" style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 440, background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 14, padding: 28, zIndex: 201, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: '#e2e8f0', fontSize: 16 }}>Nouveau contact</h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Client *</label>
              <select value={newContact.client_id} onChange={e => setNewContact(f => ({ ...f, client_id: e.target.value }))} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', borderColor: formErrors.client_id ? '#ef4444' : '#2a2d3e', cursor: 'pointer' }}>
                <option value="">— Choisir un client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            {[['prenom', 'Prénom', 'text'], ['nom', 'Nom *', 'text'], ['poste', 'Poste', 'text'], ['email', 'Email', 'email'], ['telephone', 'Téléphone', 'text'], ['mobile', 'Mobile', 'text']].map(([k, l, t]) => (
              <div key={k} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>{l}</label>
                <input type={t} value={newContact[k] || ''} onChange={e => setNewContact(f => ({ ...f, [k]: e.target.value }))} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', borderColor: formErrors[k] ? '#ef4444' : '#2a2d3e' }} />
              </div>
            ))}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>Contact principal ?</label>
              <select value={newContact.principal} onChange={e => setNewContact(f => ({ ...f, principal: Number(e.target.value) }))} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', cursor: 'pointer' }}>
                <option value={0}>Non</option><option value={1}>Oui</option>
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 20 }}>
              <button onClick={createContact} style={{ flex: 1, padding: '10px', borderRadius: 8, background: '#41B073', border: 'none', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 14, fontFamily: 'inherit' }}>Créer le contact</button>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', borderRadius: 8, background: 'transparent', border: '1px solid #2a2d3e', color: '#94a3b8', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' }}>Annuler</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
